// src/sdk/sdkManagerSetup.js
import { callbackInterfaces, WidebridgeSDK } from './sdk/index_bundle';

const { ActivityLogCallbacks, ProfileCallbacks, SipUiCallbacks, ChatCallbacks, PresenceCallbacks, XmppUiCallbacks } =
  callbackInterfaces;

// SDK manager is initialized lazily via createSdkManager
let sdkManager = null;
let sipVoiceModule = null;
let xmppManager = null;
let activityLogService = null;
let chatManager = null;
let presenceManager = null;

async function createSdkManager(credentials) {
  const { username, password, tenantId } = credentials;
  sdkManager = WidebridgeSDK.getInstance(window.wideBridgeConfig);
  if (!sdkManager.initialized) {
    if (!username) {
      const authResult = await sdkManager.authenticate('', '', tenantId || '');
      if (authResult.success) {
        await sdkManager.start(!window.name);
      } else {
        sdkManager = null;
        return false;
      }
    } else {
      const authResult_withInputCredential = await sdkManager.authenticate(username, password, tenantId);
      if (authResult_withInputCredential.success) {
        await sdkManager.start(!window.name);
      } else {
        sdkManager = null;
        const errorMsg = `Login failed`;
        throw new Error(errorMsg);
      }
    }
  }

  /*
 

  // Wait for SDK managers to be ready
  let checks = 0;
  while ((!instance.profileManager || !instance.incidentManager) && checks < 50) {
    await new Promise(resolve => setTimeout(resolve, 200));
    checks++;
  }

  if (!instance.profileManager || !instance.incidentManager) {
    throw new Error('SDK initialization timed out - managers not available');
  }

  sdkManager = instance;
  */
  return true;
}

function getSdkManager() {
  return sdkManager;
}

async function setupSdkManager({
  initContactsCallback = () => {},
  handleContactChange = contacts => {},
  handleGeofenceChange = SDKGf => {},
  handlePOIChange = SDKPoi => {},
  handleContactPresenceChange = presenceState => {},
  handleContactTagsChange = data => {},
  handleUpdateInMessages = chatBox => {},
  handleIncomingInteract = (chatBox, message) => {},
  handleUnreadCountChange = (chatBox, newCount) => {},
  handleIncidentsUpdate = updatedIncidents => {},
  handleIncomingSession = (contactIdBeforeParsing, speaking) => {},
  handlePlayIncomingVideo = stream => {},
  handleActivityRecordAdded = activityRecord => {},
  handlePTVIncomingSession = (contactIdBeforeParsing, speaking) => {},
}) {
  if (!sdkManager || !sdkManager.profileManager || !sdkManager.incidentManager) {
    console.warn('SDK Manager not initialized. Skipping setup.');
    return;
  }

  const handleOrganizationProfile = sdkOrganizationProfile => {
    if (sdkOrganizationProfile) {
      sdkManager.settings.organizationProfile.value = sdkOrganizationProfile;
    }
  };

  sdkManager.useConsoleLogging();

  const profileCallbacks = new ProfileCallbacks().addFunctions({
    onOrganizationProfileChange: () => {},
    userPreferencesFetched: userPref => {
      console.debug('CB.ProfileCallbacks - userPreferencesFetched', 'userPref', userPref);
    },
    onUserProfileChange: sdkUserProfile => {
      console.debug('sdkUserProfile', sdkUserProfile);
    },
  });
  sdkManager.profileManager.setCallbacks(profileCallbacks);
  sdkManager.profileManager.startProfileManager();
  sdkManager.profileManager.getOrganizationProfile().then(sdkOrganizationProfile => {
    handleOrganizationProfile(sdkOrganizationProfile);
  });

  // Init XMPP manager
  const xmppUiCallbacks = new XmppUiCallbacks().addFunctions({
    contactChanged: handleContactChange,
    geoFenceChanged: handleGeofenceChange,
    poiChanged: handlePOIChange,
    initContacts: initContactsCallback,
    connection: (newConnectionStatus, type) => {
      //console.debug('CB.XmppUiCallbacks - connection', 'newConnectionStatus', newConnectionStatus, 'type', type),
    },
    logout: () => console.debug('CB.XmppUiCallbacks - logout'),
  });
  xmppManager = sdkManager.xmppManager;
  xmppManager.setCallbacks(xmppUiCallbacks);
  sdkManager.exposeSDKinGlobalVar();

  const activityUiCallbacks = new ActivityLogCallbacks().addFunctions({
    onActivityRecordAdded: handleActivityRecordAdded,
  });
  activityLogService = sdkManager.activityLogService;
  activityLogService.setCallbacks(activityUiCallbacks);

  // Init SIP module

  const sipUiCallbacks = new SipUiCallbacks().addFunctions({
    stateChanged: (contactId, state) =>
      console.debug('CB.SipUiCallbacks - stateChanged', 'contactId', contactId, 'state', state),
    ptvStateChanged: (contactId, state) =>
      console.debug('CB.SipUiCallbacks - ptvStateChanged', 'contactId', contactId, 'state', state),
    pttStateChanged: (contactId, state) =>
      console.debug('CB.SipUiCallbacks - pttStateChanged', 'contactId', contactId, 'state', state),
    activeSpeaker: (activeSpeaker, isSpeaking) =>
      console.debug('CB.SipUiCallbacks - activeSpeaker', 'activeSpeaker', activeSpeaker, 'isSpeaking', isSpeaking),
    incomingSession: handleIncomingSession,
    ptvIncomingSession: (contactIdBeforeParsing, speaking) =>
      handlePTVIncomingSession(contactIdBeforeParsing, speaking),
    playIncomingVideo: handlePlayIncomingVideo,
    //stream => console.debug('CB.SipUiCallbacks - playIncomingVideo', 'stream', stream),
    incomingVideoStopped: terminate => console.debug('CB.SipUiCallbacks - playIncomingVideo', 'terminate', terminate),
    connection: (newConnectionStatus, type) =>
      console.debug('CB.SipUiCallbacks - ptvIncomingSession', 'newConnectionStatus', newConnectionStatus, 'type', type),
    sessionHealthReport: handleSessionHealthReport,
    voipCallRinging: (contactType, isPtv, contactId) => {
      console.debug('voipCallRinging', JSON.stringify(...args));
      // setVoipSession({
      //   contact: props.widebridgeSdk.contactsManager.contacts.get(contactId),
      //   side: VOIP_CALL_SIDE.OUTGOING,
      //   callStatus: VOIP_CALL_STATUS.RINGING
      // });
    },
    voipCallAnswered: (contactType, isPtv, contactId) => {
      console.debug('voipCallAnswered', JSON.stringify(...args));
      // setVoipSession({
      //   contact: props.widebridgeSdk.contactsManager.contacts.get(contactId),
      //   side: VOIP_CALL_SIDE.OUTGOING,
      //   callStatus: VOIP_CALL_STATUS.ON_CALL
      // });
    },
    voipCallFailed: (...args) => {
      console.debug('voipCallFailed', JSON.stringify(...args));
      // setVoipSession(null);
    },
    voipCallEnded: (...args) => {
      console.debug('voipCallEnded', JSON.stringify(...args));
      // setVoipSession(null);
    },
    warmUpStateChangedCallback: (contactId, state) =>
      console.debug('CB.SipUiCallbacks - warmUpStateChangedCallback', 'contactId', contactId, 'state', state),
    setMediaPermissions: () => {},
  });
  sipVoiceModule = sdkManager.sipManager;
  sipVoiceModule.setCallbacks(sipUiCallbacks);

  sipVoiceModule.startSip();

  const chatCallbacks = new ChatCallbacks().addFunctions({
    updateInMessages: handleUpdateInMessages,
    incomingSplashMessage: (chatBox, message) =>
      console.debug('CB.ChatCallbacks, incomingSplashMessage', 'chatBox', chatBox, 'message', message),
    chatStateChanged: (chatBox, typingStatus) =>
      console.debug('CB.ChatCallbacks - chatStateChanged', 'chatBox', chatBox, 'typingStatus', typingStatus),
    onUnreadCountChange: handleUnreadCountChange,
    incomingInteract: handleIncomingInteract,
    updateUIOnMessageUpdate: (chatBox, message) =>
      console.debug('CB.ChatCallbacks - updateUIOnMessageUpdate', 'chatBox', chatBox, 'message', message),
  });
  chatManager = sdkManager.chatManager;
  chatManager.setCallbacks(chatCallbacks);
  chatManager.startChatEngine();

  const presenceCallbacks = new PresenceCallbacks().addFunctions({
    onChange: handleContactPresenceChange,
    onTagsChange: data => {
      //console.debug("presence-tagchange",data)
    },
    onTagsChangeV2: handleContactTagsChange,
  });
  presenceManager = sdkManager.presenceManager;
  presenceManager.setCallbacks(presenceCallbacks);
  presenceManager.startPresenceManager();

  //incident callbacks
  sdkManager.incidentManager.onIncidentsUpdate(handleIncidentsUpdate);
}

const handleSessionHealthReport = (contactType, isPtv, contactId, sessionHealth) => {
  console.debug('sessionHealthReportData', contactType, isPtv, contactId, sessionHealth);
  // if (sessionHealth === SESSION_HEALTH.GOOD && displayPoorConnectionNotificationRef.current.find(e => e.id === contactId)) {
  //   setDisplayPoorConnectionNotification(displayPoorConnectionNotificationRef.current.filter(e => e.id !== contactId));
  // }
  //
  // if (sessionHealth === SESSION_HEALTH.BAD && !displayPoorConnectionNotificationRef.current.find(e => e.id === contactId)) {
  //   setDisplayPoorConnectionNotification([...displayPoorConnectionNotificationRef.current, contactMapRef.current.get(contactId)]);
  // }
};

export {
  sdkManager,
  sipVoiceModule,
  xmppManager,
  activityLogService,
  chatManager,
  presenceManager,
  setupSdkManager,
  createSdkManager,
  getSdkManager,
};
